import {
  useEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { useOnChange } from 'hooks/use-on-change';
import { useActiveAnchors } from './use-anchor-observer';

export type TOCThumb = [top: number, height: number];

function calcThumbPosition(container: HTMLElement, active: string[]): TOCThumb {
  if (active.length === 0 || container.clientHeight === 0) {
    return [0, 0];
  }

  let upper = Number.MAX_VALUE,
    lower = 0;

  for (const item of active) {
    const element = container.querySelector<HTMLElement>(`a[href="#${item}"]`);
    if (!element) {
      continue;
    }

    const styles = getComputedStyle(element);
    upper = Math.min(upper, element.offsetTop + parseFloat(styles.paddingTop));
    lower = Math.max(
      lower,
      element.offsetTop +
        element.clientHeight -
        parseFloat(styles.paddingBottom),
    );
  }

  return [upper, lower - upper];
}

function update(element: HTMLElement, info: TOCThumb): void {
  element.style.setProperty('--qt-top', `${info[0]}px`);
  element.style.setProperty('--qt-height', `${info[1]}px`);
}

export function TocThumb({
  containerRef,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  containerRef: RefObject<HTMLElement | null>;
}): ReactNode {
  const active = useActiveAnchors();
  const thumbRef = useRef<HTMLDivElement>(null);

  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const container = containerRef.current;

    const onResize = (): void => {
      if (!thumbRef.current) {
        return;
      }
      update(thumbRef.current, calcThumbPosition(container, activeRef.current));
    };

    onResize();
    const observer = new ResizeObserver(onResize);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  useOnChange(active, () => {
    if (!containerRef.current || !thumbRef.current) {
      return;
    }

    update(thumbRef.current, calcThumbPosition(containerRef.current, active));
  });

  return (
    <div
      ref={thumbRef}
      role="none"
      className="mt-(--qt-top) h-(--qt-height) bg-brand-600 transition-all dark:bg-gray-300"
      {...props}
    />
  );
}
